import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useState } from "react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_truth-centered/artifacts/4bkl7m0w_B2F741E8-3ED3-4C5F-8567-3D82A405954C.png";

export const Navigation = ({ mode = "builder" }) => {
  const [mobileOpen, setMobileOpen] = useState(false);

  const navLinks = [
    { to: "/", label: "Home" },
    { to: mode === "builder" ? "/builder" : "/seller", label: mode === "builder" ? "Builder Mode" : "Seller Mode" },
    { to: "/faith", label: "Faith" },
    { to: "/about", label: "About" },
    { to: "/prayer", label: "Prayer" },
    { to: "/contact", label: "Contact" },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 nav-glass" data-testid="navigation">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <img src={LOGO_URL} alt="Steward Lots" className="h-10 w-auto" />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="text-sm font-medium text-[#57534E] hover:text-[#1C1917] transition-colors"
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-[#E7E5E4]" data-testid="mobile-menu">
          <div className="px-6 py-4 space-y-3">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileOpen(false)}
                className="block py-2 text-[#57534E] hover:text-[#1C1917]"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

export const Footer = () => {
  return (
    <footer className="bg-[#0F0F10] text-[#A8A29E] py-12 px-6" data-testid="footer">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          {/* Brand */}
          <div className="md:col-span-2">
            <p className="text-sm font-medium tracking-widest text-white mb-4">STEWARD LOTS™</p>
            <p className="text-sm max-w-xs">
              Christ-led land stewardship built on Scripture, integrity, and truth.
            </p>
          </div>

          {/* Links */}
          <div>
            <h4 className="text-white font-medium mb-4">Navigate</h4>
            <div className="space-y-2 text-sm">
              <Link to="/builder" className="block hover:text-white transition-colors">Builder Mode</Link>
              <Link to="/seller" className="block hover:text-white transition-colors">Seller Mode</Link>
              <Link to="/faith" className="block hover:text-white transition-colors">Faith & Principles</Link>
              <Link to="/about" className="block hover:text-white transition-colors">About</Link>
            </div>
          </div>

          <div>
            <h4 className="text-white font-medium mb-4">Connect</h4>
            <div className="space-y-2 text-sm">
              <Link to="/contact" className="block hover:text-white transition-colors">Contact Us</Link>
              <Link to="/prayer" className="block hover:text-white transition-colors">Prayer Requests</Link>
              <Link to="/privacy" className="block hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="block hover:text-white transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>

        {/* Scripture */}
        <div className="border-t border-[#2a2a2a] pt-8">
          <p className="font-serif italic text-center text-sm">
            "The earth is the Lord's, and the fulness thereof; the world, and they that dwell therein."
          </p>
          <p className="text-center text-xs mt-2 text-[#57534E]">— Psalm 24:1</p>
        </div>

        {/* Copyright */}
        <div className="text-center text-xs mt-8 text-[#57534E]">
          © {new Date().getFullYear()} Steward Lots™. All rights reserved.
        </div>
      </div>
    </footer>
  );
};

export const ScriptureBlock = ({ verse, reference, className = "" }) => {
  return (
    <div className={`border-l-[3px] border-[#BFA15F] pl-4 ${className}`}>
      <p className="font-serif italic text-[#57534E]">{verse}</p>
      <p className="text-sm text-[#A8A29E] mt-1 uppercase tracking-wide">{reference}</p>
    </div>
  );
};

export const PageHeader = ({ title, subtitle }) => {
  return (
    <div className="text-center mb-12">
      <h1 className="font-serif text-4xl md:text-5xl text-[#1C1917] mb-4">{title}</h1>
      {subtitle && <p className="text-lg text-[#57534E] max-w-2xl mx-auto">{subtitle}</p>}
    </div>
  );
};
